import { FC, FormEvent, useEffect, useMemo, useState } from 'react';
import { AxiosResponse } from 'axios';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from '../../redux/hooks';
import { AlertSVG } from '../../components/svgs';
import { BackButton, DotLoader, Loader } from '../../components';
import BillingAddress from '../../components/payment-method-modal/BillingAddress';
import PaymentDetails from '../../components/payment-method-modal/PaymentDetails';
import { initialPaymentState } from '../../components/payment-method-modal/AddPaymentMethodModal';
import { AccountRoles } from '../../types/enums/accountRoles.enum';
import { AppRoles } from '../../types/enums/appRoles.enum';
import { Genders, PaymentActions, PaymentMethods, ShippingPaymentMethods } from '../../types/enums';
import { MarketPlaceCartItem } from '../../types/interfaces/marketPlace.interfaces';
import { PaymentMethod } from '../../types/interfaces/paymentMethod.interface';
import type {
  CardDetails,
  InstantPaymentPayload,
  MarketplacePaymentGatewayResponse,
  OrderRequest,
} from '../../types/interfaces/marketplacePayment.inteface';
import {
  BUTTON_CONSTANTS,
  LABEL_CONSTANTS,
  MARKETPLACE_CART_CONSTANTS,
  MARKETPLACE_CONSTANTS,
  NOTIFICATION_CONSTANTS,
  PAYMENT_CONSTANTS,
  TABLE_HEADER_CONSTANTS,
} from '../../constants/common';
import { toastMessages } from '../../constants/errorMessages';
import { ROUTE_PATHS } from '../../constants/routePaths';
import { deleteMarketplaceCartItems, getMarketplaceCartItems } from '../../api/marketPlaceCartItemApi';
import { makeInstantMarketplacePayment, makeMarketplacePaymentUsingCardOnFile } from '../../api/marketPlaceInventoryPaymentApi';
import { centsToDollars } from '../../services/account.service';
import { PermissionService } from '../../services/permission.service';
import { getPaymentDetails, mapPaymentInfo } from '../../services/payment.service';
import { showToast } from '../../services/toast.service';
import './marketplaceCheckout.scss';

const initialFormState = { value: '', valid: false };

const MARKETPLACE_CHECKOUT_CART_INCLUDE =
  'Accounts, MarketPlaceSpecimen.Specimen.Animal, MarketPlaceSpecimen.Specimen.SpecimenType';

type MarketplaceCheckoutLocationState = {
  selectedItems?: MarketPlaceCartItem[];
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isFemaleMarketplaceCartItem = (cartLine: MarketPlaceCartItem): boolean => {
  const gender = cartLine.marketPlaceSpecimen?.animal?.gender ?? cartLine.marketPlaceSpecimen?.specimen?.animal?.gender;
  return String(gender ?? '').toLowerCase() === Genders.Female.toLowerCase();
};

const getMarketplaceCheckoutLineTotalCents = (cartLine: MarketPlaceCartItem): number => {
  const unitPriceCents = cartLine.marketPlaceSpecimen?.unitPriceInCents ?? 0;
  const quantity = cartLine.cartQuantity ?? 0;
  return isFemaleMarketplaceCartItem(cartLine) ? unitPriceCents : unitPriceCents * quantity;
};

const MarketplaceCheckout: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { accountId } = useParams();

  const accountRoles = useAppSelector(reduxState => reduxState.auth.accountContext.roles) as AccountRoles[];
  const appRoles = useAppSelector(reduxState => reduxState.auth.appContext.roles) as AppRoles[];
  const enabled = useAppSelector(reduxState => reduxState.user.enabled);
  const verified = useAppSelector(reduxState => reduxState.user.verified);

  const permissionService = useMemo(
    () => new PermissionService(accountRoles, appRoles, enabled, verified),
    [accountRoles, appRoles, enabled, verified],
  );

  const accountIdNumber = useMemo(() => {
    if (!accountId) return undefined;
    const parsedAccountId = Number(accountId);
    return Number.isFinite(parsedAccountId) ? parsedAccountId : undefined;
  }, [accountId]);

  const initialLocationState = (location.state as MarketplaceCheckoutLocationState | null)?.selectedItems;
  const [checkoutCartItems, setCheckoutCartItems] = useState<MarketPlaceCartItem[]>(() =>
    Array.isArray(initialLocationState) && initialLocationState.length > 0 ? initialLocationState : [],
  );
  const [cartLoading, setCartLoading] = useState(() => !(Array.isArray(initialLocationState) && initialLocationState.length > 0));

  const [isLoadingPayment, setIsLoadingPayment] = useState<boolean>(true);

  const [paymentInfo, setPaymentInfo] = useState<PaymentMethod>({ ...initialPaymentState, paymentType: initialFormState });
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>({ ...initialPaymentState, paymentType: initialFormState });
  const [marketplacePaymentMethod, setMarketplacePaymentMethod] = useState<string>(ShippingPaymentMethods.NEW_CARD);

  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [validate, setValidate] = useState(false);
  const [isMakingPayment, setIsMakingPayment] = useState(false);

  const isAchOnFile = paymentInfo?.paymentType?.value === PaymentMethods.ACH;
  const hasCardOnFile = paymentInfo?.paymentType?.value === PaymentMethods.CARD;

  const marketplaceTotalQty = useMemo(
    () =>
      checkoutCartItems.reduce(
        (runningTotal: number, cartLine: MarketPlaceCartItem) => runningTotal + (cartLine.cartQuantity ?? 0),
        0,
      ),
    [checkoutCartItems],
  );

  const marketplaceTotalCents = useMemo(() => {
    return checkoutCartItems.reduce((runningTotal: number, cartLine: MarketPlaceCartItem) => {
      return runningTotal + getMarketplaceCheckoutLineTotalCents(cartLine);
    }, 0);
  }, [checkoutCartItems]);

  const marketplaceTotalDollars = useMemo(() => {
    const dollarsValue = centsToDollars(marketplaceTotalCents);
    return typeof dollarsValue === 'number' ? dollarsValue.toFixed(2) : dollarsValue;
  }, [marketplaceTotalCents]);

  const orderRequests = useMemo((): OrderRequest[] => {
    return checkoutCartItems.map(cartLine => {
      const unitPriceCents = cartLine.marketPlaceSpecimen?.unitPriceInCents ?? 0;
      const quantity = cartLine.cartQuantity ?? 0;
      const totalAmountCents = isFemaleMarketplaceCartItem(cartLine) ? unitPriceCents : unitPriceCents * quantity;

      return {
        sellerAccountId:
          cartLine.marketPlaceSpecimen?.account?.accountId ??
          cartLine.marketPlaceSpecimen?.accountId ??
          cartLine.sellerAccount?.accountId ??
          cartLine.sellerAccountId ??
          0,
        totalAmountCents,
        specimenSelection: {
          specimenId: cartLine.marketPlaceSpecimen?.specimen?.specimenId ?? 0,
          quantity,
          ...(isFemaleMarketplaceCartItem(cartLine)
            ? {
                selectedEmbryoStrawIds: cartLine.marketPlaceSpecimen?.embryoStrawId
                  ? [cartLine.marketPlaceSpecimen.embryoStrawId]
                  : [],
              }
            : {}),
        },
      };
    });
  }, [checkoutCartItems]);

  useEffect(() => {
    if (!cartLoading) return;
    if (accountIdNumber == null) {
      setCartLoading(false);
      return;
    }

    let cancelled = false;

    const fetchMarketplaceCartItems = async () => {
      try {
        const { data } = await getMarketplaceCartItems({
          include: MARKETPLACE_CHECKOUT_CART_INCLUDE,
          filter: `accountId eq ${accountIdNumber}`,
        });
        if (cancelled) return;
        const items = data ?? [];
        setCheckoutCartItems(items);
        if (items.length === 0) {
          showToast.error(toastMessages.SOMETHING_WENT_WRONG);
          navigate(ROUTE_PATHS.APP_TRANSACTION_CART, { replace: true });
        }
      } catch {
        if (!cancelled) showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        if (!cancelled) setCartLoading(false);
      }
    };
    void fetchMarketplaceCartItems();

    return () => {
      cancelled = true;
    };
  }, [accountIdNumber, cartLoading, navigate]);

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      if (accountIdNumber == null) {
        setIsLoadingPayment(false);
        return;
      }

      setIsLoadingPayment(true);
      try {
        const paymentData = await getPaymentDetails(accountIdNumber, permissionService.userHasRSGAdminPermission());
        paymentData && setPaymentInfo(paymentData);
      } catch {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setIsLoadingPayment(false);
      }
    };

    void fetchPaymentDetails();
  }, [accountId, accountIdNumber, permissionService]);

  useEffect(() => {
    paymentInfo != undefined && setPaymentMethod(paymentInfo);
    if (paymentInfo?.paymentType?.value === PaymentMethods.CARD) {
      setMarketplacePaymentMethod(ShippingPaymentMethods.CARD_ON_FILE);
    } else {
      setMarketplacePaymentMethod(ShippingPaymentMethods.NEW_CARD);
      setPaymentMethod(initialPaymentState);
    }
  }, [paymentInfo]);

  useEffect(() => {
    if (marketplacePaymentMethod === ShippingPaymentMethods.CARD_ON_FILE && paymentInfo) {
      setPaymentMethod(paymentInfo);
    } else if (marketplacePaymentMethod === ShippingPaymentMethods.NEW_CARD) {
      setPaymentMethod(initialPaymentState);
    }
  }, [marketplacePaymentMethod, paymentInfo]);

  const isFormValid = useMemo(() => {
    const isCardPayment = paymentMethod?.paymentType?.value === PaymentMethods.CARD;
    const isCardOnFile = marketplacePaymentMethod === ShippingPaymentMethods.CARD_ON_FILE;
    const isCardValid =
      paymentMethod?.cardNumber?.valid && paymentMethod?.expiry?.valid && (isCardOnFile || paymentMethod?.cvv?.valid);
    const isBillingAddressValid =
      paymentMethod?.address?.valid &&
      paymentMethod?.city?.valid &&
      paymentMethod?.region?.valid &&
      paymentMethod?.postalCode?.valid &&
      paymentMethod?.country?.valid;

    return Boolean(
      paymentMethod?.paymentType?.valid && paymentMethod?.name?.valid && isBillingAddressValid && isCardPayment && isCardValid,
    );
  }, [paymentMethod, marketplacePaymentMethod]);

  const handlePlaceOrder = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isPlacingOrder || isMakingPayment) return;

    setValidate(true);

    if (!isFormValid || orderRequests.length === 0) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      return;
    }

    if (accountIdNumber == null) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      return;
    }

    setIsPlacingOrder(true);

    const orders = orderRequests;
    const cardOnFile = marketplacePaymentMethod === ShippingPaymentMethods.CARD_ON_FILE;

    try {
      setIsMakingPayment(true);

      let paymentResponse: AxiosResponse<unknown>;

      if (cardOnFile) {
        paymentResponse = await makeMarketplacePaymentUsingCardOnFile(accountIdNumber, { orders });
      } else {
        const mappedPaymentInfo = mapPaymentInfo(paymentMethod);
        const card: CardDetails = {
          account: mappedPaymentInfo.account ?? '',
          address: mappedPaymentInfo.address ?? '',
          city: mappedPaymentInfo.city ?? '',
          country: mappedPaymentInfo.country ?? '',
          cvv2: mappedPaymentInfo.cvv2 ?? '',
          expiry: mappedPaymentInfo.expiry ?? '',
          name: mappedPaymentInfo.name ?? '',
          paymentMethod: mappedPaymentInfo.paymentMethod ?? '',
          postal: mappedPaymentInfo.postal ?? '',
          region: mappedPaymentInfo.region ?? '',
        };
        const instantPayload: InstantPaymentPayload = {
          card,
          payment: { orders },
        };
        paymentResponse = await makeInstantMarketplacePayment(accountIdNumber, instantPayload);
      }

      const data = paymentResponse?.data;
      const httpOk = paymentResponse?.status >= 200 && paymentResponse?.status < 300;
      const noContentBody = data === '' || data == null || (isRecord(data) && Object.keys(data).length === 0);
      const gatewayBody: MarketplacePaymentGatewayResponse | undefined =
        data && isRecord(data) ? (data as MarketplacePaymentGatewayResponse) : undefined;
      const paymentSucceeded =
        (httpOk && noContentBody) || gatewayBody?.transactionDetails?.status === 'A' || gatewayBody?.status === 'A';

      if (paymentSucceeded) {
        showToast.success(toastMessages.PAYMENT_SUCCESS);

        const cartItemIds = checkoutCartItems
          .map(checkoutLine => checkoutLine.marketPlaceCartItemId)
          .filter((cartItemId): cartItemId is number => typeof cartItemId === 'number' && cartItemId > 0);

        if (cartItemIds.length > 0) {
          try {
            await deleteMarketplaceCartItems(cartItemIds);
          } catch {
            showToast.error(toastMessages.SOMETHING_WENT_WRONG);
          }
        }

        navigate(ROUTE_PATHS.APP_MARKETPLACE);
      } else {
        showToast.error(toastMessages.PAYMENT_FAIL);
      }
    } catch (error: unknown) {
      type AxiosErrorShape = { response?: { status?: number; data?: { title?: string } } };
      const axiosLike = error as AxiosErrorShape;
      const errorTitle = axiosLike.response?.status === 701 ? axiosLike.response?.data?.title : undefined;
      if (errorTitle) showToast.error(errorTitle);
      else showToast.error(toastMessages.PAYMENT_FAIL);
    } finally {
      setIsMakingPayment(false);
      setIsPlacingOrder(false);
    }
  };

  if (cartLoading) {
    return <Loader loaderSize="medium" pageLoader />;
  }

  return (
    <div className="marketplace-checkout">
      <BackButton onClick={() => navigate(ROUTE_PATHS.APP_TRANSACTION_CART)} label={BUTTON_CONSTANTS.BACK} />
      <div className="marketplace-checkout__card">
        <div className="marketplace-checkout__summary">
          <h2>{MARKETPLACE_CART_CONSTANTS.MARKETPLACE_CART_SECTION_TITLE}</h2>
          <div className="marketplace-checkout__summary-row">
            <span>{TABLE_HEADER_CONSTANTS.TOTAL_QTY}</span>
            <span>{marketplaceTotalQty}</span>
          </div>
          <div className="marketplace-checkout__summary-row">
            <span>{MARKETPLACE_CONSTANTS.TOTAL_PRICE}</span>
            <span>${marketplaceTotalDollars}</span>
          </div>
        </div>

        <div className="marketplace-checkout__payment shipping-payment-information add-payment-method">
          <div className="payment-info-header">
            <h2>{PAYMENT_CONSTANTS.PAYMENT_INFORMATION}</h2>
          </div>

          {isAchOnFile && (
            <div className="payment-info-subheader ach-warning">
              <AlertSVG />
              <label>{NOTIFICATION_CONSTANTS.ACH_PAYMENT_NOT_ALLOWED_MARKETPLACE}</label>
            </div>
          )}

          {isLoadingPayment ? (
            <Loader loaderSize="small" addedSpace />
          ) : (
            <form action="submit" onSubmit={handlePlaceOrder}>
              <div className="payment-method-selection">
                {paymentInfo?.paymentType?.value !== PaymentMethods.ACH && <label>{LABEL_CONSTANTS.CHOOSE_PAYMENT_MODE}</label>}
                <div className="radio-row">
                  {hasCardOnFile && (
                    <>
                      <input
                        type="radio"
                        id="marketplace-payment-method-card-on-file"
                        name="marketplace-payment-method"
                        value={ShippingPaymentMethods.CARD_ON_FILE}
                        onChange={() => setMarketplacePaymentMethod(ShippingPaymentMethods.CARD_ON_FILE)}
                        checked={marketplacePaymentMethod === ShippingPaymentMethods.CARD_ON_FILE}
                      />
                      <label htmlFor="marketplace-payment-method-card-on-file">{LABEL_CONSTANTS.USE_CARD_ON_FILE}</label>
                    </>
                  )}

                  <input
                    type="radio"
                    id="marketplace-payment-method-new-card"
                    name="marketplace-payment-method"
                    value={ShippingPaymentMethods.NEW_CARD}
                    onChange={() => setMarketplacePaymentMethod(ShippingPaymentMethods.NEW_CARD)}
                    checked={marketplacePaymentMethod === ShippingPaymentMethods.NEW_CARD}
                  />
                  <label htmlFor="marketplace-payment-method-new-card">{LABEL_CONSTANTS.ENTER_NEW_CARD_DETAILS}</label>
                </div>
              </div>

              {(isPlacingOrder || isMakingPayment) && <DotLoader />}

              <PaymentDetails
                paymentMethod={paymentMethod}
                validate={validate}
                buttonAction={PaymentActions.SHIPPING_PAYMENT}
                paymentMethodType={marketplacePaymentMethod}
                setPaymentMethod={setPaymentMethod}
              />
              <BillingAddress
                paymentMethod={paymentMethod}
                validate={validate}
                buttonAction={PaymentActions.SHIPPING_PAYMENT}
                paymentMethodType={marketplacePaymentMethod}
                setPaymentMethod={setPaymentMethod}
              />

              <div className="payment-transaction-note">
                {paymentMethod?.paymentType?.value === PaymentMethods.CARD && (
                  <div className="transaction-alert">
                    <AlertSVG />
                    <p>
                      {NOTIFICATION_CONSTANTS.CC_PROCESSING_FEE_MSG} {MARKETPLACE_CONSTANTS.PURCHASES_ARE_FINAL}
                    </p>
                  </div>
                )}
              </div>

              <div className="buttons">
                <button
                  type="button"
                  className="button red inverted medium"
                  onClick={() => navigate(ROUTE_PATHS.APP_TRANSACTION_CART)}>
                  {BUTTON_CONSTANTS.CANCEL}
                </button>
                <button
                  type="submit"
                  disabled={orderRequests.length === 0 || !isFormValid || isPlacingOrder || isMakingPayment}
                  className="button green medium">
                  {BUTTON_CONSTANTS.PLACE_ORDER}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketplaceCheckout;
